'use client';

import { useCallback, useEffect, useRef } from 'react';
import type CardLocation from '@alltrails/amplitude/enums/CardLocation';
import logTripPlannerCTAClicked from '@alltrails/amplitude/events/logTripPlannerCTAClicked';
import logTripPlannerCTAViewed from '@alltrails/amplitude/events/logTripPlannerCTAViewed';
import useUser from '@alltrails/context/hooks/useUser';
import Button from '@alltrails/denali/components/Button';
import SparkleFilled from '@alltrails/denali/icons/SparkleFilled';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import { canCreateTripPlan } from '@alltrails/layout/utils/tripPlannerAvailability';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setIsShowingMobileList } from '@/utils/slices/exploreSlice';
import { resetTripPlannerItinerary } from '@/utils/slices/tripPlannerSlice';
import styles from './TripPlannerMobileTryCta.module.scss';
import useTripPlannerEntryContext from './useTripPlannerEntryContext';

type TripPlannerMobileTryCtaProps = {
  /** Which mobile view this instance belongs to. */
  showIn: 'list' | 'map';
};

const ENTRY_POINTS: Record<TripPlannerMobileTryCtaProps['showIn'], CardLocation> = {
  list: 'trip planner trail list',
  map: 'trip planner map'
};

/**
 * "Try Trip Assistant" CTA for free users on mobile, floating beside the list/map toggle. The
 * sidebar footer button is desktop-only, since on mobile it sat underneath this floating row.
 */
const TripPlannerMobileTryCta = ({ showIn }: TripPlannerMobileTryCtaProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useUser();
  const entryContext = useTripPlannerEntryContext();
  const isTripPlannerV2Enabled = useExperiments()['web-disco-rm-trip-planner-v2']?.value === 'on';
  const isShowingMobileList = useAppSelector(state => state.explore.isShowingMobileList);
  const hasItinerary = useAppSelector(state => state.tripPlanner.status === 'success' && !!state.tripPlanner.result);

  const entryPoint = ENTRY_POINTS[showIn];
  const matchesView = isTripPlannerV2Enabled || (showIn === 'list' ? isShowingMobileList : !isShowingMobileList);
  const showCta = matchesView && hasItinerary && !canCreateTripPlan(currentUser);

  const hasLoggedViewRef = useRef(false);
  useEffect(() => {
    if (!showCta) {
      hasLoggedViewRef.current = false;
    } else if (!hasLoggedViewRef.current) {
      hasLoggedViewRef.current = true;
      logTripPlannerCTAViewed({ ...entryContext, entry_point: entryPoint });
    }
  }, [entryContext, entryPoint, showCta]);

  const onClick = useCallback(() => {
    logTripPlannerCTAClicked({ ...entryContext, entry_point: entryPoint });
    dispatch(resetTripPlannerItinerary());
    dispatch(setIsShowingMobileList(true));
  }, [dispatch, entryContext, entryPoint]);

  if (!showCta) {
    return null;
  }

  return (
    <Button
      className={styles.cta}
      icon={{ Component: SparkleFilled }}
      onClick={onClick}
      size="sm"
      // TODO: translations: https://alltrails.atlassian.net/browse/DISCO-26230 - the trip planner is en-US only
      text="Try Trip Assistant"
      variant="secondary"
    />
  );
};

export default TripPlannerMobileTryCta;
